import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { routes } from './app.routes';
import { BrowserModule } from '@angular/platform-browser';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { provideNoopAnimations } from '@angular/platform-browser/animations';

import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { authInterceptor } from './interceptors/auth.interceptor';
import { DatePipe } from '@angular/common';
import { errorInterceptor } from './interceptors/error.interceptor';
import { SharedDataService } from './shared/services/shared-data-service';
import { loaderInterceptor } from './interceptors/loader.interceptor';
import { GlobalErrorHandlerService } from './shared/services/global-error-handler.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AuthorizationUtility } from './shared/authorization/auth.utility';
import { PPL_ACTIONS_ADAPTER, TOASTER_NOTIFICATION_SERVICE } from '@ppl/ui';
import { NextgenActionsAdapter } from './shared/adapters/ppl-actions.adapter';
import { 
  provideListConfigResolver, 
  provideListDataLoader, 
  provideListSharedState,
  NextgenListTableAdapter 
} from './shared/adapters/ppl-list-table.adapter';
import { NotificationService } from './shared/services/notification.service';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeuix/themes/lara';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

interface Config {
  clientId: string;
  authority: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  knownAuthorities?: string[];
}

export function loggerCallback(logLevel: LogLevel, message: string) {
}

export function MSALInstanceFactory(): IPublicClientApplication {
  const config: Config = {
    clientId: environment.msalConfig.auth.clientId,
    authority: environment.b2cPolicies.authorities.signUpSignIn.authority ? environment.b2cPolicies.authorities.signUpSignIn.authority : environment.msalConfig.auth.authority,
    redirectUri: '/',
    postLogoutRedirectUri: '/',
  };

  if (environment.b2cPolicies.authorities.signUpSignIn.authority) {
    config['knownAuthorities'] = [environment.b2cPolicies.authorityDomain]
  }

  return new PublicClientApplication({
    auth: config,
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set(
    environment.apiConfig.uri,
    environment.apiConfig.scopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

Chart.register(ChartDataLabels);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withInMemoryScrolling({scrollPositionRestoration: 'top'})),
    importProvidersFrom(
      BrowserModule,
      /* MatButtonModule,
      MatToolbarModule,
      MatListModule,
      MatMenuModule */
    ),
    provideNoopAnimations(),
    provideHttpClient(withInterceptorsFromDi(), withFetch(), withInterceptors([authInterceptor, errorInterceptor, loaderInterceptor])),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    { 
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    DatePipe,
    SharedDataService,
    NzModalService,
    AuthorizationUtility,
    { provide: PPL_ACTIONS_ADAPTER, useClass: NextgenActionsAdapter },
    // Bridge ppl-ui ToasterComponent to existing NotificationService
    { provide: TOASTER_NOTIFICATION_SERVICE, useExisting: NotificationService },
    provideListConfigResolver(),
    provideListDataLoader(),
    provideListSharedState(),
    NextgenListTableAdapter,
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: false
        }
      },
      // Ensure PrimeNG overlays aren't clipped by scroll containers like `.ppl-content-container`.
      overlayAppendTo: 'body',
      // PrimeNG's own z-index layers. Only applies to raw PrimeNG overlays —
      // ppl-ui components (dropdown-select, datepicker, etc.) override via OverlayPositionUtil.
      // Dialogs: 1050+, CDK overlay (nz-select): 1200 (styles.css), Alertbox/Loader: 9999.
      zIndex: {
        modal: 1000,
        overlay: 1050,
        menu: 1000,
        tooltip: 1100,
      }
    })
  ],
};
